import type { Aabb, WorldManifest } from "@townsy/contracts";
import { districtOfPoint, fencedPlotIds, townBounds as worldTownBounds, townColliders, townDrivableRects as worldTownDrivableRects, townManifest, yardDecorationColliders, type PlacementRef } from "@townsy/world";

/**
 * Town v6 (ADR-0042 decisions 1 and 3): the whole town is ONE street room, built from the whole manifest. Riverside and
 * Hillcrest are neighbourhoods of it: every plot, street and item keeps its `districtId`, and a visitor's district is
 * derived from where they stand (`districtOfPoint`).
 *
 * The browser's street geometry is EXACTLY what the one realtime street room uses: packages/world's whole-town helpers
 * (slice 1, town.ts: `townColliders` + `yardDecorationColliders`, `townSeats` with its bench ids, `townDrivableRects`,
 * `townBounds`), each handed the whole manifest. Prediction (colliders, bounds, drivable rects), the seat list and the
 * 3D street build from them; the district slices (`districtManifest`) stay for per-district data (names, streets,
 * squares, the backdrop round each district). This module is the one place the browser names them.
 */

/** The town's bounds: the union of every district's bounds (world `townBounds`). */
export function townBounds(m: WorldManifest): Aabb {
  return worldTownBounds(m);
}

/**
 * The whole town's street colliders for an owned set and the yards: `townColliders(m, owned, fenced)` +
 * `yardDecorationColliders(m, yard)`, the realtime town street room's inputs (a front fence replaces that plot's hedges).
 */
export function townStreetColliders(m: WorldManifest, owned: ReadonlySet<string>, yard: ReadonlyArray<PlacementRef & { propertyId: string }>): Aabb[] {
  const whole = townManifest(m);
  return [...townColliders(whole, owned, fencedPlotIds(yard)), ...yardDecorationColliders(whole, yard)];
}

/**
 * Road + crosswalk rects of the whole town, one cached array per manifest (drive prediction: the world caches its road
 * outline by array identity, so the same instance is always handed over).
 */
export function townDrivableRects(m: WorldManifest): readonly Aabb[] {
  return worldTownDrivableRects(m);
}

/**
 * The district a point lies in (ADR-0042: the HUD's "here", the map, the directory default), or `fallback` outside
 * every district's bounds (behind a closed wall, beyond the town's edge).
 */
export function districtAt(m: WorldManifest, x: number, z: number, fallback: string): string {
  return districtOfPoint(m, x, z) ?? fallback;
}

/** A district's display name ("Riverside"), or the id when the manifest does not know it. */
export function districtName(m: Pick<WorldManifest, "districts">, districtId: string): string {
  return m.districts.find((d) => d.id === districtId)?.name ?? districtId;
}
